import { useCallback, useMemo, useState } from 'react';
import { JSONContent } from '@tiptap/core';

import { SettingsHr } from 'approot/activity-builder/settings/settings.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  DRAFT_ACTION_COMPONENT_AUDIO_ADD,
  DRAFT_ACTION_COMPONENT_AUDIO_UPDATE,
  DRAFT_ACTION_COMPONENT_CLEAR_TEXT,
} from 'global/constants';
import {
  SettingProps,
  InteractiveResponseCategoriseItem,
} from '../interactive-response-categorise.constants';
import { useInteractiveResponseCategoriseUpdate } from './interactive-response-categorise.hooks';
import {
  SortableContent,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable.style';
import { COLOURS } from '../../interactive-response-shared.constants';
import ComponentAudioModal from 'approot/activity-builder/components/shared/component-audio-modal/component-audio-modal';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import InteractiveResponseRichtextEdit from '../../shared/interactive-response-richtext';
import InteractiveResponseAttachAudio from '../../shared/interactive-response-attach-audio';
import InteractiveResponseDeleteItem from '../../shared/interactive-response-delete-item';
import { ColourTokenButton } from 'approot/shared/colour/colour-token';

const InteractiveResponseCategoriseItemsText = (
  props: SettingProps & {
    item: InteractiveResponseCategoriseItem;
    'data-selector'?: string;
  }
) => {
  const { properties, item, 'data-selector': dataSelector } = props;

  const [editingId, setEditingId] = useState<string | null>(null);

  const { loading, handleUpdate } =
    useInteractiveResponseCategoriseUpdate(props);

  const {
    isVisible: isAudioModalVisible,
    openModal: openAudioModal,
    closeModal: closeAudioModal,
  } = useSimpleModalState();

  const selectedItem = useMemo(
    () => (editingId === item.itemId ? item : null),
    [editingId, item]
  );

  const handleUpdateItemText = useCallback(
    ({
      itemId,
      text,
      richtext,
      baseColour,
    }: {
      itemId: string;
      text: string | null;
      richtext: JSONContent | null;
      baseColour: string | null;
    }) => {
      const items = properties.interactiveResponseCategorise.items.map(i => {
        if (i.itemId === itemId) {
          return {
            ...i,
            text,
            richtext,
            baseColour,
          };
        }
        return i;
      });

      handleUpdate({
        ...properties,
        interactiveResponseCategorise: {
          ...properties.interactiveResponseCategorise,
          items,
        },
      });
    },
    [properties, handleUpdate]
  );

  return (
    <>
      {isAudioModalVisible && (
        <ComponentAudioModal
          loading={loading}
          fileKey={selectedItem?.audio?.key || ''}
          fileName={selectedItem?.audio?.fileName || ''}
          original={selectedItem?.audio?.original || null}
          closeModal={closeAudioModal}
          onUpdate={data => {
            handleUpdate(
              {
                ...properties,
                interactiveResponseCategorise: {
                  ...properties.interactiveResponseCategorise,
                  items: properties.interactiveResponseCategorise.items.map(
                    item => {
                      if (item.itemId === editingId) {
                        return {
                          ...item,
                          audio: data
                            ? {
                                __typename: 'InteractiveResponseAudio',
                                itemId: item.itemId,
                                key: data.key || '',
                                fileName: data.fileName || '',
                                original: data.original || null,
                              }
                            : null,
                        };
                      }
                      return item;
                    }
                  ),
                },
              },
              {
                draftAction: selectedItem?.audio?.key
                  ? DRAFT_ACTION_COMPONENT_AUDIO_UPDATE
                  : DRAFT_ACTION_COMPONENT_AUDIO_ADD,
                isSaveImmediate: true,
                onCompleted: closeAudioModal,
              }
            );
          }}
        />
      )}
      <SettingsHr />
      <SortableItem as="li" data-selector={dataSelector}>
        <SortableHeader>
          <div />
          <InteractiveResponseDeleteItem
            loading={loading}
            onDelete={onCompleted =>
              handleUpdate(
                {
                  ...properties,
                  interactiveResponseCategorise: {
                    ...properties.interactiveResponseCategorise,
                    items:
                      properties.interactiveResponseCategorise.items.filter(
                        q => q.itemId !== item.itemId
                      ),
                  },
                },
                {
                  isSaveImmediate: true,
                  draftAction: DRAFT_ACTION_COMPONENT_CLEAR_TEXT,
                  onCompleted,
                }
              )
            }
          />
        </SortableHeader>
        <SortableContent>
          <InteractiveResponseRichtextEdit
            data={item.richtext || null}
            onChange={json =>
              handleUpdateItemText({
                itemId: item.itemId,
                text: item.text,
                richtext: json,
                baseColour: item.baseColour,
              })
            }
          />
        </SortableContent>
        <SettingsHr $marginBottom={0} />
        <SortableContent>
          <Box $gap={12} $flexWrap="nowrap">
            {COLOURS.map(colour => (
              <ColourTokenButton
                key={colour.base}
                colours={colour}
                size={32}
                isActive={item.baseColour === colour.base}
                onClick={() =>
                  handleUpdateItemText({
                    itemId: item.itemId,
                    text: item.text,
                    richtext: item.richtext,
                    baseColour: colour.base,
                  })
                }
              />
            ))}
          </Box>
        </SortableContent>
        <SettingsHr $marginBottom={0} />
        <SortableContent>
          <InteractiveResponseAttachAudio
            audioFileName={item.audio?.fileName || null}
            onClick={() => {
              setEditingId(item.itemId);
              openAudioModal();
            }}
          />
        </SortableContent>
      </SortableItem>
    </>
  );
};

export default InteractiveResponseCategoriseItemsText;
